import {defineStore} from 'pinia'
import {v4 as uuidv4} from 'uuid'
import type {Maybe} from '../types'
import type {Ref} from 'vue'
import {isNullOrUndefined} from '../utils/utils'

interface ModalAction {
  callback?: () => void | Promise<void>
  label?: Ref | string
  icon?: Ref | string | false
  variant?: Ref | string
  visible?: Ref | boolean
  class?: Ref | string
}

interface SubmitModalAction extends ModalAction {
  enabled?: Ref | boolean
}

export type ModalSize = 'medium' | 'large' | 'full' | 'extra-large'

interface Modal {
  id: string
  title: string
  message?: string
  submit?: SubmitModalAction
  cancel?: ModalAction
  data?: any
  size?: ModalSize
  cancelCallbackOnClear?: boolean
  closeOnSubmit?: boolean
  noPadding?: boolean
  noGap?: boolean
  allowOverflow?: boolean
}

type State = {
  currentModal: Maybe<Modal>
}

export const useModalStore = defineStore('modal', {
  state(): State {
    return {
      currentModal: undefined,
    }
  },
  actions: {
    addModal(
      title: string,
      message?: string,
      submit?: SubmitModalAction,
      cancel?: ModalAction,
      data?: any,
      cancelCallbackOnClear = true,
      size?: ModalSize,
      closeOnSubmit = true
    ) {
      this.clearModal(cancelCallbackOnClear)
      this.currentModal = {id: uuidv4(), title, message, submit, cancel, data, size, cancelCallbackOnClear, closeOnSubmit}
      return this.currentModal.id
    },
    hasModalWithId(id: Maybe<string>) {
      return !isNullOrUndefined(this.currentModal) && this.currentModal.id === id
    },
    clearModal(cancelCallbackOnClear = false) {
      if (cancelCallbackOnClear && this.currentModal?.cancel?.callback) {
        this.currentModal.cancel.callback()
      }

      this.currentModal = null
    },
    addSubmit(id: string, sma: Partial<SubmitModalAction>) {
      if (this.hasModalWithId(id)) {
        this.currentModal!.submit = {...this.currentModal!.submit, ...sma}
        return true
      }

      return false
    },
  },
  getters: {
    hasModal: (state) => !!state.currentModal,
    modalData: (state) => state.currentModal?.data,
  },
})
